import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, change } from 'redux-form';
import { t } from 'i18next';
import { format, subDays } from 'date-fns';
import { formatDisplayDate } from '../../utils/func';
import { apiCancelOrder } from '../../services/orders';
import { routes } from '../../utils/constants';
import { ShipmentStatuses } from '../../utils/enums';
import Download from '../../assets/icons/Download.svg';
import Filter from '../../assets/icons/Filter16x16.svg';
import ActionsBlack from '../../assets/icons/ActionsBlack.svg';
import XIcon from '../../assets/icons/x_icon.svg';
import { StyledMDBDropdownMenu } from '../_styledComponents/MDBDropdownToggle';
import { getDeliveryDate } from '../../utils/dateTime';
import { ApplyFiltersBody, ApplyFiltersFooter } from './ApplyFilters';
import DownloadModal from './DownloadModal';
import Table from './Table/index';
import LeftSideBar from '../ReactComponent/LeftSideBar';
import {
  DEFAULT_COLUMNS, LABEL_COLUMNS_TRANSLATION_KEY, TABS, SERVICE_LEVELS_CODES, FILTERS_SERVICE_LEVEL, FILTERS_SHIPMENT_SERVICE_TYPE,
  FILTERS_STATUSES, ADDITIONAL_COLUMNS, SORTING_DIRECTION, FILTER_MAPPING,
} from './utils';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../lib/ui/buttons/SecondaryButton';
import {
  Container, TitlesContainer, StyledMDBDropdownActions, StyledMDBDropdownToggle, DropdownItem, DropdownItemIcon, Title, SubTitle,
  ModalBoxStyle, ModalBody, CloseContainer, ModalTitlesContainer, ModalTitle, ModalSubTitle, ButtonGroup, ModalError,
} from './css';
import { apiGetQuotesAll } from '../../services/quotes';
import { apiDownloadQuoteReport } from '../../services/documents';
import ActionsMenu from '../../lib/ui/actionsMenu';

const formName = 'ManageQuotes';

const filterInitialValues = {
  accountNumbers: [],
  statusCodes: [],
  serviceLevels: [],
  serviceTypes: [],
  fromDate: null,
  toDate: null,
};
const initialValues = {
  filters: filterInitialValues,
  columns: DEFAULT_COLUMNS,
  rememberSelection: false,
};

const PAGE_SIZE = 10;

const Dropdown = ({
  status, quoteNumber, pending, isApproved,
}) => {
  const actions = [
    {
      id: 'duplicate-quote',
      label: t('DuplicateQuote'),
      icon: 'DuplicateIcon',
      disabled: (status === ShipmentStatuses.UnderReview || pending),
      action: () => window.open(`${routes.createQuote}?quoteNumber=${quoteNumber}`),
      visible: true,
    },
    {
      id: 'print-summary',
      label: t('PrintSummary'),
      icon: 'PrintIcon',
      disabled: pending || (status === ShipmentStatuses.Expired && !isApproved),
      action: () => apiDownloadQuoteReport(quoteNumber),
      visible: true,
    },
    {
      id: 'create-shipment',
      label: t('CreateShipment'),
      icon: 'CreateShipmentIcon',
      disabled: pending,
      action: () => window.open(`${routes.createShipment}?quoteNumber=${quoteNumber}`),
      visible: true,
    },
    {
      id: 'resubmit-quote',
      label: t('ResubmitQuote'),
      icon: 'ResubmitQuoteIcon',
      disabled: (status === ShipmentStatuses.UnderReview || pending),
      action: () => window.open(`${routes.createQuote}?resubmit=true&quoteNumber=${quoteNumber}`),
      visible: true,
    },
  ];

  return (<ActionsMenu options={actions} />);
};

// eslint-disable-next-line no-unused-vars
export const DropdownActions = ({ handleDownloadReportClick, toggleCustomizeMenu, toggleFilterMenu }) => (
  <StyledMDBDropdownActions className="dropdown">
    <StyledMDBDropdownToggle className="border-0">
      <img src={ActionsBlack} alt="Actions" />
      <span>{t('Actions')}</span>
    </StyledMDBDropdownToggle>
    <StyledMDBDropdownMenu className="text-white bg-dark" basic>
      <>
        <DropdownItem onClick={handleDownloadReportClick}>
          <DropdownItemIcon iconSrc={Download} />
          {t('DownloadReport')}
        </DropdownItem>
        <DropdownItem onClick={toggleFilterMenu}>
          <DropdownItemIcon iconSrc={Filter} />
          {t('Filters')}
        </DropdownItem>
      </>
    </StyledMDBDropdownMenu>
  </StyledMDBDropdownActions>
);

const CancelModalContent = ({
  isAuthenticated, errorMessage, isCancelShipmentWarning, isCancelShipmentSuccess, closeModal, handleCancelShipmentRequest,
}) => {
  const LTLCustomerNumber = 'LTL: 1.866.Day.Ross (329-7677)';
  const CommerceCustomerNumber = 'Commerce: 1.877.726.3329';

  return (
    <>
      <ModalTitlesContainer>
        <ModalTitle>
          {!isCancelShipmentWarning
            ? t('CancelShipment')
            : isCancelShipmentWarning
              ? t('CancelShipmentWarning')
              : t('Error')}
        </ModalTitle>

        {errorMessage && !isCancelShipmentWarning ? (
          <ModalSubTitle>{errorMessage}</ModalSubTitle>
        ) : (
          <ModalError>
            <p><strong>{errorMessage}</strong></p>
            <p>{t('PleaseContactCustomerService')}</p>
            <p>{(errorMessage || !isAuthenticated) ? LTLCustomerNumber : t('')}</p>
            <p>{(errorMessage || !isAuthenticated) ? CommerceCustomerNumber : t('')}</p>
          </ModalError>
        )}
      </ModalTitlesContainer>

      <ButtonGroup style={{ marginTop: '64px' }}>
        {(errorMessage && isAuthenticated && !isCancelShipmentWarning && !isCancelShipmentSuccess) ? (
          <>
            <div className="col-sm-12 col-md">
              <SecondaryButton
                label={t('GoBack')}
                onClick={() => closeModal()}
                showArrow={false}
              />
            </div>
            <div className="col-sm-12 col-md">
              <PrimaryButton
                label={t('CancelShipment')}
                onClick={() => handleCancelShipmentRequest()}
              />
            </div>
          </>
        ) : (errorMessage && isAuthenticated && (isCancelShipmentWarning || isCancelShipmentSuccess)) ? (
          <div className="col-sm-12">
            <PrimaryButton
              label={t('Close')}
              onClick={() => closeModal()}
            />
          </div>
        ) : (errorMessage) ? (
          <div className="col-sm-12">
            <SecondaryButton
              label={t('Accept')}
              onClick={() => closeModal()}
            />
          </div>
        ) : null}
      </ButtonGroup>
    </>
  );
};

let ManageQuotes = ({
  fields, dispatch, isAuthenticated, formData = { filters: {} }, profileAccountNumbers,
}) => {
  const [data, setData] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isCustomizeMenuOpen, setIsCustomizeMenuOpen] = useState(false);
  const [isDownloadReportModalOpen, setIsDownloadReportModalOpen] = useState(false);
  const [isCancelShipmentModalOpen, setIsCancelShipmentModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCancelShipmentWarning, setIsCancelShipmentWarning] = useState(false);
  const [isCancelShipmentSuccess, setIsCancelShipmentSuccess] = useState(false);
  const [cancelShipmentZipCode, setCancelShipmentZipCode] = useState(null);
  const [cancelShipmentOrderNumber, setCancelShipmentOrderNumber] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.ALL);
  const [keySearch, setKeySearch] = useState('');

  const [fieldOrder, setFieldOrder] = useState({
    [TABS.ALL]: {},
    [TABS.IN_PROGRESS]: {},
    [TABS.COMPLETED]: {},
  });
  const [pageNumber, setPageNumber] = useState({
    [TABS.ALL]: 1,
    [TABS.IN_PROGRESS]: 1,
    [TABS.COMPLETED]: 1,
  });
  const [totalPages, setTotalPages] = useState({
    [TABS.ALL]: 1,
    [TABS.IN_PROGRESS]: 1,
    [TABS.COMPLETED]: 1,
  });

  const firstLoadFiltersObject = {
    accountNumbers: (profileAccountNumbers || []).slice(0, 20),
    statusCodes: [],
    serviceLevels: FILTERS_SERVICE_LEVEL[activeTab].map((serviceLevel) => serviceLevel),
    serviceTypes: [],
    fromDate: format(subDays(new Date(), 60), 'yyyy-MM-dd'),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  };

  const defaultTabColumns = ((formData && formData.columns && formData.columns[activeTab]) || DEFAULT_COLUMNS[activeTab]);
  const { field: fieldOrderingName, direction: fieldOrderingDirection } = fieldOrder[activeTab];

  const getServiceLevelCodes = (serviceLevels) => serviceLevels.reduce((acc, value) => {
    acc = acc.concat(SERVICE_LEVELS_CODES[value]);
    return acc;
  }, []);

  const getTotalPages = (numberOfOrders) => Math.floor((numberOfOrders / PAGE_SIZE) + ((numberOfOrders % PAGE_SIZE) === 0 ? 0 : 1));

  const convertDataFormat = (data) => data && data.map((each) => ({
    proBillNumber: each.probillNumber,
    shipmentNumber: each.shipmentNumber,
    shipperName: each.shipper.name,
    shipperAddress: each.shipper.address1,
    shipperCity: each.shipper.city,
    shipperState: each.shipper.provinceCode,
    shipperZipCode: each.shipper.postalCode,
    consigneeName: each.consignee.name,
    consigneeAddress: each.consignee.address1,
    consigneeCity: each.consignee.city,
    consigneeState: each.consignee.provinceCode,
    consigneeZipCode: each.consignee.postalCode,
    estimatedDelivery: each.estimatedDeliveryDate && formatDisplayDate(each.estimatedDeliveryDate),
    delivered: each.deliveryDate && getDeliveryDate({ deliveryDate: each.deliveryDate, deliveryDateType: each.deliveryDateType }),
    created: each.createdDate && formatDisplayDate(each.createdDate),
    status: each.isApproved || each.expired ? each.status : t('StatusPENDING'),
    quote: each.quoteNumber,
    service: each.serviceLevelCode,
    expiry: each.expiryDate && formatDisplayDate(each.expiryDate),
    pallets: each.pallets,
    pieces: each.pieces,
    weight: `${each.weight.value} ${each.weight.unitOfMeasure}`,
    statusPercentage: each.statusPercentage,
    billOfLading: each.billOfLading,
    originTerminal: each.originTerminal,
    destinationTerminal: each.destinationTerminal,
    palletsDelivery: each.pallets,
    piecesDelivery: each.pieces,
    weightDelivery: each.weight && each.weight.value,
    charge: each.charge && each.charge.value,
    poNumber: each.poNumber,
    customerReferenceNumber: each.customerReferenceNumber,
    deliveryAppointment: each.deliveryAppointment,
    actions: <Dropdown
      activeTab={activeTab}
      status={each.status}
      pending={!each.isApproved && !each.expired}
      quoteNumber={each.quoteNumber}
      isApproved={each.isApproved}
    />,
  }));

  const fetchFullOrders = () => {
    const {
      accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate,
    } = firstLoadFiltersObject;
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);

    return Promise.all([
      apiGetQuotesAll(
        accountNumbers,
        statusCodes,
        serviceLevelCodes,
        serviceTypes,
        fromDate,
        toDate,
        PAGE_SIZE,
        pageNumber[TABS.ALL],
        fieldOrderingName,
        fieldOrderingDirection,
      ),
    ]).then((res) => {
      if (res && res[0].status === 200) {
        const resAllOrders = res[0].data;

        const convertedAllOrders = convertDataFormat(resAllOrders.quotesItems);
        setData(convertedAllOrders);
        setAllOrders(convertedAllOrders);
        setTotalPages({
          [TABS.ALL]: getTotalPages(resAllOrders.total),
        });
      }
    }).catch(() => {}).finally(() => {
      setIsLoaded(true);
      dispatch(change(formName, 'filters', firstLoadFiltersObject));
    },
    );
  };

  const fetchAllOrders = () => {
    const {
      filters: {
        accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate,
      },
    } = formData;
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);

    apiGetQuotesAll(
      accountNumbers,
      statusCodes,
      serviceLevelCodes,
      serviceTypes,
      fromDate,
      toDate,
      PAGE_SIZE,
      pageNumber[TABS.ALL],
      fieldOrderingName,
      fieldOrderingDirection,
      keySearch,
    ).then((res) => {
      if (res && res.status === 200) {
        const convertedData = convertDataFormat(res.data.quotesItems);

        setData(convertedData);
        setAllOrders(convertedData);
        setTotalPages({ ...totalPages, [TABS.ALL]: getTotalPages(res.data.total) });
      }
    });
  };

  useEffect(() => {
    fetchFullOrders();
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    switch (activeTab) {
      case TABS.ALL:
      default:
        // eslint-disable-next-line consistent-return
        return fetchAllOrders();
    }
  }, [pageNumber, fieldOrder, keySearch]);

  useEffect(() => {
    switch (activeTab) {
      case TABS.ALL:
      default:
        setData(allOrders);
        break;
    }
  }, [activeTab]);

  const numberOfFilters = useMemo(() => {
    let result = 0;
    if (formData && formData.filters) {
      Object.keys(formData.filters).forEach((key) => {
        if (formData.filters[key] && formData.filters[key].length) {
          if (Array.isArray(formData.filters[key])) {
            result += formData.filters[key].length;
          } else {
            result++;
          }
        }
      });
    }

    return result;
  }, [formData.filters]);

  const handleDownloadReportClick = () => {
    setIsDownloadReportModalOpen(true);
  };

  const closeDownloadReportModal = () => {
    setIsDownloadReportModalOpen(false);
  };

  const handleCancelShipmentRequest = () => {
    apiCancelOrder(cancelShipmentZipCode, cancelShipmentOrderNumber).then((res) => {
      setErrorMessage(res.message);
      setIsCancelShipmentWarning(res.hasReturnedError);
      setIsCancelShipmentSuccess(!res.hasReturnedError);
    });
  };

  const closeCancelShipmentModal = () => {
    setErrorMessage(null);
    setIsCancelShipmentWarning(false);
    setIsCancelShipmentSuccess(false);
    setCancelShipmentZipCode(null);
    setCancelShipmentOrderNumber(null);
    setIsCancelShipmentModalOpen(false);
  };

  const accountNumbersArray = (profileAccountNumbers || []).map((accountNumber) => ({
    label: accountNumber,
    value: accountNumber,
  }));

  // Filters.
  const clearFilters = () => {
    dispatch(change(formName, 'filters', filterInitialValues));
  };

  const applyFilters = () => {
    setPageNumber({ ...pageNumber, [activeTab]: 1 });
    setIsFiltersMenuOpen(false);
  };

  const changeActiveTab = (updatedTab) => {
    clearFilters();
    setActiveTab(updatedTab);
  };

  const onFieldOrderingClick = (columnName) => {
    if (!fieldOrderingName && !fieldOrderingDirection) {
      setFieldOrder({
        ...fieldOrder,
        [activeTab]: {
          field: FILTER_MAPPING[columnName],
          fieldName: columnName,
          direction: SORTING_DIRECTION.ASC,
        },
      });
    } else if (fieldOrderingName && fieldOrderingDirection === SORTING_DIRECTION.ASC) {
      setFieldOrder({
        ...fieldOrder,
        [activeTab]: {
          field: FILTER_MAPPING[columnName],
          fieldName: columnName,
          direction: SORTING_DIRECTION.DESC,
        },
      });
    } else {
      setFieldOrder({ ...fieldOrder, [activeTab]: {} });
    }
  };

  const handleSearch = (searchStr) => {
    const str = (searchStr && searchStr.length > 0) ? searchStr : '';
    setPageNumber(1);
    setKeySearch(str);
  };

  return (
    <Container>
      <Modal
        isOpen={isDownloadReportModalOpen || isCancelShipmentModalOpen}
        onRequestClose={isDownloadReportModalOpen ? closeDownloadReportModal : closeCancelShipmentModal}
        style={ModalBoxStyle}
        contentLabel="Modal"
      >
        <CloseContainer>
          <img src={XIcon} alt={t('Close')} onClick={isDownloadReportModalOpen ? closeDownloadReportModal : closeCancelShipmentModal} aria-hidden />
        </CloseContainer>
        <ModalBody>
          {isDownloadReportModalOpen ? (
            <DownloadModal
              columns={defaultTabColumns.concat(ADDITIONAL_COLUMNS)}
              defaultColumns={defaultTabColumns}
              accountNumbers={accountNumbersArray}
              closeModal={closeDownloadReportModal}
            />
          ) : (
            <CancelModalContent
              isAuthenticated={isAuthenticated}
              errorMessage={errorMessage}
              isCancelShipmentWarning={isCancelShipmentWarning}
              isCancelShipmentSuccess={isCancelShipmentSuccess}
              closeModal={closeCancelShipmentModal}
              handleCancelShipmentRequest={handleCancelShipmentRequest}
            />
          )}
        </ModalBody>
      </Modal>

      <LeftSideBar
        isOpen={isFiltersMenuOpen}
        toggleMenu={setIsFiltersMenuOpen}
        footer={(
          <ApplyFiltersFooter
            clearFilters={clearFilters}
            applyFilters={applyFilters}
            disabled={
              (formData.filters.accountNumbers && (formData.filters.accountNumbers.length === 0 || formData.filters.accountNumbers.length > 20))
            }
          />
        )}
      >
        <ApplyFiltersBody
          filters={{
            serviceLevels: {
              name: 'filters.serviceLevels',
              options: FILTERS_SERVICE_LEVEL[activeTab].map((serviceLevel) => ({
                label: t(`ServiceType${serviceLevel}`),
                value: serviceLevel,
              })),
            },
            serviceTypes: {
              name: 'filters.serviceTypes',
              options: FILTERS_SHIPMENT_SERVICE_TYPE[activeTab].map((shipmentServiceType) => ({
                label: t(`ShipmentServiceType${shipmentServiceType}`),
                value: shipmentServiceType,
              })),
            },
            statusCodes: {
              name: 'filters.statusCodes',
              options: FILTERS_STATUSES[activeTab].map((status) => ({
                label: t(`Status${status}`),
                value: status,
              })),
            },
            fromDate: {
              name: 'filters.fromDate',
              filteredValues: null,
            },
            toDate: {
              name: 'filters.toDate',
              filteredValues: null,
            },
            accountNumbers: {
              name: 'filters.accountNumbers',
              options: accountNumbersArray,
            },
          }}
          data={formData.filters}
        />
      </LeftSideBar>

      <TitlesContainer>
        <Title>{fields.heading.value}</Title>
        <SubTitle>{fields.description.value}</SubTitle>
      </TitlesContainer>

      <Table
        data={data}
        columns={defaultTabColumns.map((column) => ({
          name: column,
          accessor: `${column === 'shipper' ? 'shipperName' : column === 'consignee' ? 'consigneeName' : column}`,
          label: t(`Column${LABEL_COLUMNS_TRANSLATION_KEY[column]}`),
          disableSortBy: (column === 'actions'),
        }))}
        activeTab={activeTab}
        changeActiveTab={changeActiveTab}
        handleDownloadReportClick={handleDownloadReportClick}
        setIsCustomizeMenuOpen={setIsCustomizeMenuOpen}
        setIsFiltersMenuOpen={setIsFiltersMenuOpen}
        numberOfFilters={numberOfFilters}
        pageNumber={pageNumber[activeTab]}
        onPageChange={(newPageNumber) => setPageNumber({ ...pageNumber, [activeTab]: newPageNumber })}
        totalPages={totalPages[activeTab]}
        fieldOrder={fieldOrder}
        changeFieldOrder={onFieldOrderingClick}
        handleSearch={handleSearch}
      />
    </Container>
  );
};

const mstp = (state) => ({
  formData: getFormValues(formName)(state),
  isAuthenticated: state.profile.isAuthenticated,
  profileAccountNumbers: state.profile.accountNumbers,
  allShipments: state.user.allShipments,
});

ManageQuotes.propTypes = {
  fields: PropTypes.object,
  dispatch: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  formData: PropTypes.object,
  profileAccountNumbers: PropTypes.array,
};

ManageQuotes = reduxForm({
  form: formName,
  initialValues,
})(ManageQuotes);

export default connect(mstp)(ManageQuotes);

Dropdown.propTypes = {
  status: PropTypes.string,
  quoteNumber: PropTypes.string,
  pending: PropTypes.bool,
  isApproved: PropTypes.bool,
};

DropdownActions.propTypes = {
  handleDownloadReportClick: PropTypes.func,
  toggleCustomizeMenu: PropTypes.func,
  toggleFilterMenu: PropTypes.func,
};

CancelModalContent.propTypes = {
  isAuthenticated: PropTypes.bool,
  errorMessage: PropTypes.string,
  isCancelShipmentWarning: PropTypes.bool,
  isCancelShipmentSuccess: PropTypes.bool,
  closeModal: PropTypes.func,
  handleCancelShipmentRequest: PropTypes.func,
};
